import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Straighten,
  Scale,
  Thermostat,
  LocalDrink,
  SquareFoot,
  Speed,
  Schedule,
  Storage,
  SwapHoriz,
  CheckCircle,
} from '@mui/icons-material';
import { 
  trackConversion, 
  trackCategoryChange, 
  trackUnitSwap 
} from '../services/analytics.ts';

interface ConversionCategory {
  name: string;
  units: { [key: string]: number };
}

const conversionCategories: { [key: string]: ConversionCategory } = {
  length: {
    name: 'Length',
    units: {
      'Meter': 1,
      'Kilometer': 1000,
      'Centimeter': 0.01,
      'Millimeter': 0.001,
      'Mile': 1609.34,
      'Yard': 0.9144,
      'Foot': 0.3048,
      'Inch': 0.0254,
    }
  },
  weight: {
    name: 'Weight',
    units: {
      'Kilogram': 1,
      'Gram': 0.001,
      'Pound': 0.453592,
      'Ounce': 0.0283495,
      'Ton': 1000,
      'Stone': 6.35029,
    }
  },
  temperature: {
    name: 'Temperature',
    units: {
      'Celsius': 1,
      'Fahrenheit': 1,
      'Kelvin': 1,
    }
  },
  volume: {
    name: 'Volume',
    units: {
      'Liter': 1,
      'Milliliter': 0.001,
      'Gallon': 3.78541,
      'Quart': 0.946353,
      'Pint': 0.473176,
      'Cup': 0.236588,
      'Tablespoon': 0.0147868,
      'Teaspoon': 0.00492892,
    }
  },
  area: {
    name: 'Area',
    units: {
      'Square Meter': 1,
      'Square Kilometer': 1000000,
      'Square Centimeter': 0.0001,
      'Square Mile': 2589988.11,
      'Acre': 4046.86,
      'Square Yard': 0.836127,
      'Square Foot': 0.092903,
      'Square Inch': 0.00064516,
    }
  },
  speed: {
    name: 'Speed',
    units: {
      'Meter per Second': 1,
      'Kilometer per Hour': 0.277778,
      'Mile per Hour': 0.44704,
      'Knot': 0.514444,
      'Foot per Second': 0.3048,
    }
  },
  time: {
    name: 'Time',
    units: {
      'Second': 1,
      'Minute': 60,
      'Hour': 3600,
      'Day': 86400,
      'Week': 604800,
      'Month': 2592000,
      'Year': 31536000,
    }
  },
  data: {
    name: 'Data',
    units: {
      'Byte': 1,
      'Kilobyte': 1024,
      'Megabyte': 1048576,
      'Gigabyte': 1073741824,
      'Terabyte': 1099511627776,
      'Bit': 0.125,
      'Kilobit': 128,
      'Megabit': 131072,
      'Gigabit': 134217728,
    }
  },
};

// AnimatedIcon component for tab icons
const AnimatedIcon = ({ icon: Icon, selected }: { icon: React.ElementType, selected: boolean }) => (
  <span
    style={{
      display: 'inline-flex',
      transition: 'transform 0.3s cubic-bezier(0.4,1.3,0.7,1)',
      transform: selected ? 'scale(1.3)' : 'scale(1)',
      color: 'inherit',
    }}
  >
    <Icon sx={{ fontSize: 20 }} />
  </span>
);

const UnitConverter: React.FC = () => {
  // Initialize state with values from localStorage or defaults
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const saved = localStorage.getItem('unit-converter-category');
    return saved && conversionCategories[saved] ? saved : 'length';
  });
  
  const [fromUnit, setFromUnit] = useState(() => {
    const category = localStorage.getItem('unit-converter-category') || 'length';
    const saved = localStorage.getItem(`unit-converter-from-unit-${category}`);
    const units = Object.keys(conversionCategories[category].units);
    return saved && units.includes(saved) ? saved : units[0];
  });
  
  const [toUnit, setToUnit] = useState(() => {
    const category = localStorage.getItem('unit-converter-category') || 'length';
    const saved = localStorage.getItem(`unit-converter-to-unit-${category}`);
    const units = Object.keys(conversionCategories[category].units);
    return saved && units.includes(saved) ? saved : (units[1] || units[0]);
  });
  
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [preferencesRestored, setPreferencesRestored] = useState(false);

  const currentCategory = conversionCategories[selectedCategory];

  // Handle initial load and preference restoration
  useEffect(() => {
    const savedCategory = localStorage.getItem('unit-converter-category');
    if (savedCategory && conversionCategories[savedCategory]) {
      setPreferencesRestored(true);
      // Clear the indicator after a short delay
      setTimeout(() => setPreferencesRestored(false), 2000);
    }
    
    // Track component load
    console.log('🔍 UnitConverter: Component loaded with category:', selectedCategory);
    try {
      const { trackAppUsage } = require('../services/analytics.ts');
      trackAppUsage('Component Load', `UnitConverter: ${selectedCategory}`, 1);
      console.log('✅ UnitConverter: Component load tracked');
    } catch (error) {
      console.error('❌ UnitConverter: Error tracking component load:', error);
    }
  }, [selectedCategory]);

  const convertTemperature = useCallback((value: number, from: string, to: string): number => {
    // Convert to Celsius first
    let celsius: number;
    switch (from) {
      case 'Celsius':
        celsius = value;
        break;
      case 'Fahrenheit':
        celsius = (value - 32) * 5/9;
        break;
      case 'Kelvin':
        celsius = value - 273.15;
        break;
      default:
        celsius = value;
    }

    // Convert from Celsius to target unit
    switch (to) {
      case 'Celsius':
        return celsius;
      case 'Fahrenheit':
        return (celsius * 9/5) + 32;
      case 'Kelvin':
        return celsius + 273.15;
      default:
        return celsius;
    }
  }, []);

  const convertValue = useCallback((value: string, from: string, to: string, category: string) => {
    if (!value || isNaN(Number(value))) {
      setToValue('');
      return;
    }

    const numValue = parseFloat(value);

    if (category === 'temperature') {
      // Special handling for temperature conversions
      const result = convertTemperature(numValue, from, to);
      setToValue(result.toFixed(6));
    } else {
      // Standard conversion for other categories
      const fromFactor = currentCategory.units[from];
      const toFactor = currentCategory.units[to];
      const result = (numValue * fromFactor) / toFactor;
      setToValue(result.toFixed(6));
    }

    // Track conversion event
    if (value && from && to) {
      trackConversion(category, from, to, value);
    }
  }, [currentCategory, convertTemperature]);

  useEffect(() => {
    convertValue(fromValue, fromUnit, toUnit, selectedCategory);
  }, [convertValue, fromValue, fromUnit, toUnit, selectedCategory]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    console.log('🔍 UnitConverter: Category change detected:', { from: selectedCategory, to: newValue });
    
    setSelectedCategory(newValue);
    
    // Save category to localStorage
    localStorage.setItem('unit-converter-category', newValue);
    
    const units = Object.keys(conversionCategories[newValue].units);
    
    // Try to restore previously used units for this category, or use defaults
    const savedFromUnit = localStorage.getItem(`unit-converter-from-unit-${newValue}`);
    const savedToUnit = localStorage.getItem(`unit-converter-to-unit-${newValue}`);
    
    const newFromUnit = savedFromUnit && units.includes(savedFromUnit) ? savedFromUnit : units[0];
    const newToUnit = savedToUnit && units.includes(savedToUnit) ? savedToUnit : (units[1] || units[0]);
    
    setFromUnit(newFromUnit);
    setToUnit(newToUnit);
    
    // Save the units for this category
    localStorage.setItem(`unit-converter-from-unit-${newValue}`, newFromUnit);
    localStorage.setItem(`unit-converter-to-unit-${newValue}`, newToUnit);
    
    setFromValue('');
    setToValue('');
    
    // Track category change with enhanced analytics
    console.log('🔍 UnitConverter: Tracking category change to:', newValue);
    trackCategoryChange(newValue);
    
    // Also track as a page view for better analytics visibility
    try {
      const { trackPageView } = require('../services/analytics.ts');
      trackPageView(`/unit-converter/${newValue}`, `${conversionCategories[newValue].name} Converter`);
      console.log('✅ UnitConverter: Page view tracked for category:', newValue);
    } catch (error) {
      console.error('❌ UnitConverter: Error tracking page view:', error);
    }
  };

  const handleFromUnitChange = (event: any) => {
    const newFromUnit = event.target.value;
    console.log('🔍 UnitConverter: From unit changed:', { from: fromUnit, to: newFromUnit, category: selectedCategory });
    
    setFromUnit(newFromUnit);
    // Save the from unit for this category
    localStorage.setItem(`unit-converter-from-unit-${selectedCategory}`, newFromUnit);
    
    // Track unit selection
    try {
      const { trackAppUsage } = require('../services/analytics.ts');
      trackAppUsage('Unit Selection', `From: ${newFromUnit} (${selectedCategory})`, 1);
      console.log('✅ UnitConverter: From unit selection tracked');
    } catch (error) {
      console.error('❌ UnitConverter: Error tracking from unit selection:', error);
    }
  };

  const handleToUnitChange = (event: any) => {
    const newToUnit = event.target.value;
    console.log('🔍 UnitConverter: To unit changed:', { from: toUnit, to: newToUnit, category: selectedCategory });
    
    setToUnit(newToUnit);
    // Save the to unit for this category
    localStorage.setItem(`unit-converter-to-unit-${selectedCategory}`, newToUnit);
    
    // Track unit selection
    try {
      const { trackAppUsage } = require('../services/analytics.ts');
      trackAppUsage('Unit Selection', `To: ${newToUnit} (${selectedCategory})`, 1);
      console.log('✅ UnitConverter: To unit selection tracked');
    } catch (error) {
      console.error('❌ UnitConverter: Error tracking to unit selection:', error);
    }
  };

  const handleFromValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFromValue(newValue);
    
    // Track value input (only if there's a meaningful value)
    if (newValue && newValue.trim() !== '' && parseFloat(newValue) > 0) {
      console.log('🔍 UnitConverter: Value entered:', { value: newValue, category: selectedCategory });
      
      // Track value input (debounced to avoid too many events)
      setTimeout(() => {
        try {
          const { trackAppUsage } = require('../services/analytics.ts');
          trackAppUsage('Value Input', `${selectedCategory}: ${newValue}`, parseFloat(newValue));
          console.log('✅ UnitConverter: Value input tracked');
        } catch (error) {
          console.error('❌ UnitConverter: Error tracking value input:', error);
        }
      }, 1000); // 1 second debounce
    }
  };

  const swapUnits = () => {
    const newFromUnit = toUnit;
    const newToUnit = fromUnit;
    
    console.log('🔍 UnitConverter: Units swapped:', { 
      from: `${fromUnit} → ${newFromUnit}`, 
      to: `${toUnit} → ${newToUnit}`, 
      category: selectedCategory 
    });
    
    setFromUnit(newFromUnit);
    setToUnit(newToUnit);
    setFromValue(toValue);
    
    // Save the swapped units for this category
    localStorage.setItem(`unit-converter-from-unit-${selectedCategory}`, newFromUnit);
    localStorage.setItem(`unit-converter-to-unit-${selectedCategory}`, newToUnit);
    
    // Track unit swap with enhanced analytics
    console.log('🔍 UnitConverter: Tracking unit swap');
    trackUnitSwap(selectedCategory, fromUnit, toUnit);
    
    // Also track as app usage for better visibility
    try {
      const { trackAppUsage } = require('../services/analytics.ts');
      trackAppUsage('Unit Swap', `${selectedCategory}: ${fromUnit} ↔ ${toUnit}`, 1);
      console.log('✅ UnitConverter: Unit swap tracked');
    } catch (error) {
      console.error('❌ UnitConverter: Error tracking unit swap:', error);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        align="center" 
        sx={{ 
          mb: 4,
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700,
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        Unit Converter
      </Typography>

      {/* Preferences restored notification */}
      {preferencesRestored && (
        <Box sx={{ 
          textAlign: 'center', 
          mb: 2,
          animation: 'fadeInOut 2s ease-in-out',
          '@keyframes fadeInOut': {
            '0%': { opacity: 0, transform: 'translateY(-10px)' },
            '20%': { opacity: 1, transform: 'translateY(0)' },
            '80%': { opacity: 1, transform: 'translateY(0)' },
            '100%': { opacity: 0, transform: 'translateY(-10px)' }
          }
        }}>
          <Typography 
            variant="body2" 
            color="success.main" 
            sx={{ 
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <CheckCircle sx={{ fontSize: 16 }} />
            Your preferences have been restored
          </Typography>
        </Box>
      )}

      <Paper 
        elevation={3} 
        sx={{ 
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '& .MuiTabs-scrollButtons': {
              color: 'primary.main',
            }
          }}
        >
          <Tab 
            icon={<AnimatedIcon icon={Straighten} selected={selectedCategory === 'length'} />} 
            label={conversionCategories.length.name} 
            value="length"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={Scale} selected={selectedCategory === 'weight'} />} 
            label={conversionCategories.weight.name} 
            value="weight"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={Thermostat} selected={selectedCategory === 'temperature'} />} 
            label={conversionCategories.temperature.name} 
            value="temperature"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={LocalDrink} selected={selectedCategory === 'volume'} />} 
            label={conversionCategories.volume.name} 
            value="volume"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={SquareFoot} selected={selectedCategory === 'area'} />} 
            label={conversionCategories.area.name} 
            value="area"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={Speed} selected={selectedCategory === 'speed'} />} 
            label={conversionCategories.speed.name} 
            value="speed"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={Schedule} selected={selectedCategory === 'time'} />} 
            label={conversionCategories.time.name} 
            value="time"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
          <Tab 
            icon={<AnimatedIcon icon={Storage} selected={selectedCategory === 'data'} />} 
            label={conversionCategories.data.name} 
            value="data"
            sx={{
              minWidth: 120,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              }
            }}
          />
        </Tabs>
      </Paper>

      <Card 
        elevation={4}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Unit Selection Row */}
            <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel id="from-unit-label" sx={{ color: 'text.secondary' }}>From</InputLabel>
                <Select
                  value={fromUnit}
                  labelId="from-unit-label"
                  label="From"
                  onChange={handleFromUnitChange}
                  aria-describedby="from-unit-description"
                  sx={{
                    '& .MuiSelect-select': {
                      fontWeight: 600,
                    },
                    '&:focus-within': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    }
                  }}
                >
                  {Object.keys(currentCategory.units).map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
                <Typography id="from-unit-description" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select the unit to convert from
                </Typography>
              </FormControl>

              <Box sx={{ width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconButton
                  onClick={swapUnits}
                  color="primary"
                  size="large"
                  aria-label="Swap units"
                  title="Swap from and to units"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    boxShadow: 2,
                    alignSelf: 'center',
                    marginTop: '6px',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    m: 0,
                  }}
                >
                  <SwapHoriz sx={{ fontSize: 32 }} />
                </IconButton>
              </Box>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel id="to-unit-label" sx={{ color: 'text.secondary' }}>To</InputLabel>
                <Select
                  value={toUnit}
                  labelId="to-unit-label"
                  label="To"
                  onChange={handleToUnitChange}
                  aria-describedby="to-unit-description"
                  sx={{
                    '& .MuiSelect-select': {
                      fontWeight: 600,
                    },
                    '&:focus-within': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    }
                  }}
                >
                  {Object.keys(currentCategory.units).map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
                <Typography id="to-unit-description" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select the unit to convert to
                </Typography>
              </FormControl>
            </Box>

            <Divider sx={{ borderColor: 'divider', borderWidth: 1 }} />

            {/* Value Input Row */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Enter value"
                type="number"
                value={fromValue}
                onChange={handleFromValueChange}
                placeholder="0"
                aria-describedby="from-value-description"
                InputProps={{
                  sx: {
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }
                }}
                inputProps={{
                  'aria-label': `Enter value in ${fromUnit}`,
                  step: 'any',
                  min: '0'
                }}
              />
              <Typography id="from-value-description" variant="caption" color="text.secondary" sx={{ position: 'absolute', top: '100%', left: 0 }}>
                Enter the value you want to convert from {fromUnit}
              </Typography>

              <Typography 
                variant="h4" 
                color="primary.main" 
                aria-label="equals sign"
                sx={{ 
                  px: 3,
                  fontWeight: 700,
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                =
              </Typography>

              <TextField
                sx={{ 
                  flex: 1, 
                  minWidth: 200,
                  '& .MuiInputBase-root': {
                    bgcolor: 'background.default',
                  }
                }}
                label="Result"
                type="text"
                value={toValue}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }
                }}
                inputProps={{
                  'aria-label': `Converted value in ${toUnit}`,
                  'aria-readonly': 'true'
                }}
                aria-describedby="result-description"
              />
              <Typography id="result-description" variant="caption" color="text.secondary" sx={{ position: 'absolute', top: '100%', right: 0 }}>
                {toValue ? `Converted result: ${toValue} ${toUnit}` : 'Result will appear here'}
              </Typography>

              {/* Live region for screen readers */}
              <Box
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  position: 'absolute',
                  left: '-10000px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden'
                }}
              >
                {toValue && `${fromValue} ${fromUnit} equals ${toValue} ${toUnit}`}
              </Box>
            </Box>
          </Box>

          {fromValue && toValue && (
            <Box 
              sx={{ 
                mt: 4, 
                p: 3, 
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                borderRadius: 2,
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Conversion Result
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                {fromValue} {fromUnit} = {toValue} {toUnit}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UnitConverter; 